import { Mode, tokens, fonts } from "./theme";
import { useLanguage } from "@/contexts/LanguageContext";

interface Props { mode?: Mode }

const HomeMockup = ({ mode = "dark" }: Props) => {
  const T = tokens(mode);
  const isDark = mode === "dark";
  const { t } = useLanguage();

  const stats = [
    { label: t("mock.home.stat1Label"), value: "7", sub: t("mock.home.stat1Sub"), serif: false },
    { label: t("mock.home.stat2Label"), value: "#134", sub: t("mock.home.stat2Sub"), serif: true },
    { label: t("mock.home.stat3Label"), value: t("mock.home.stat3Value"), sub: t("mock.home.stat3Sub"), serif: true },
  ];

  return (
    <div style={{ background: isDark ? `linear-gradient(160deg, ${T.surface} 0%, #1A1F26 100%)` : T.surface, color: T.text, fontFamily: fonts.ui, height: "100%", padding: "56px 20px 28px", overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 22 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 38, height: 38, borderRadius: 999, background: T.surface2, border: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ width: 8, height: 8, borderRadius: 999, background: T.accent }} />
          </div>
          <div>
            <div style={{ fontSize: 9, color: T.textDim, letterSpacing: 1.5, textTransform: "uppercase", fontWeight: 600 }}>{t("mock.home.hi")}</div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Lina</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <div style={{ background: T.surface2, color: T.text, borderRadius: 999, padding: "5px 10px", fontSize: 11, fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ color: T.coral }}>◉</span> 12
          </div>
          <div style={{ background: T.accent, color: T.accentInk, borderRadius: 999, padding: "5px 10px", fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}>
            ◈ 1'247
          </div>
        </div>
      </div>

      <div style={{ fontSize: 10, color: T.accent, letterSpacing: 1.5, textTransform: "uppercase", fontWeight: 600, marginBottom: 8 }}>
        {t("mock.home.chapter")}
      </div>

      <h2 style={{ fontFamily: fonts.display, fontSize: 36, fontWeight: 400, letterSpacing: -1, lineHeight: 1.0, margin: "0 0 22px", color: T.text }}>
        {t("mock.home.headline1")}<br />{t("mock.home.headline2")} <em style={{ color: T.accent, fontStyle: "italic" }}>{t("mock.home.headline3")}</em>.
      </h2>

      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 18, padding: 16, marginBottom: 14, display: "flex", alignItems: "center", gap: 14 }}>
        <div style={{ position: "relative", width: 56, height: 56, flexShrink: 0 }}>
          <svg width="56" height="56" viewBox="0 0 56 56">
            <circle cx="28" cy="28" r="22" fill="none" stroke={T.surface2} strokeWidth="5" />
            <circle cx="28" cy="28" r="22" fill="none" stroke={T.accent} strokeWidth="5" strokeLinecap="round" strokeDasharray={`${0.6 * 2 * Math.PI * 22} ${2 * Math.PI * 22}`} transform="rotate(-90 28 28)" />
          </svg>
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: T.text }}>60%</div>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 9, color: T.textDim, letterSpacing: 1.2, textTransform: "uppercase", fontWeight: 600, marginBottom: 2 }}>{t("mock.home.continueAt")}</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 2 }}>{t("mock.home.scenario")}</div>
          <div style={{ fontSize: 10, color: T.textDim }}>{t("mock.home.scenarioMeta")}</div>
        </div>
      </div>

      <button style={{ width: "100%", background: T.accent, color: T.accentInk, border: "none", borderRadius: 16, padding: "14px 18px", fontSize: 12, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, cursor: "pointer", fontFamily: fonts.ui }}>
        {t("mock.home.startLesson")} <span>→</span>
      </button>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 18 }}>
        {stats.map((s, i) => (
          <div key={i} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: "10px 10px" }}>
            <div style={{ fontSize: 8, color: T.textDim, letterSpacing: 1.2, textTransform: "uppercase", fontWeight: 600 }}>{s.label}</div>
            <div style={{ fontFamily: s.serif ? fonts.display : fonts.ui, fontSize: s.serif ? 22 : 18, fontWeight: s.serif ? 400 : 700, color: T.text, margin: "2px 0" }}>{s.value}</div>
            <div style={{ fontSize: 8, color: T.textMute }}>{s.sub}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{t("mock.home.todayTitle")}</div>
        <div style={{ fontSize: 10, color: T.textDim }}>{t("mock.home.todayCount")}</div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ background: T.coral, color: "#0E1114", borderRadius: 14, padding: 12, display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 10, background: "rgba(0,0,0,0.12)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>🔥</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700 }}>{t("mock.home.streakTitle")}</div>
            <div style={{ fontSize: 10, opacity: 0.75 }}>{t("mock.home.streakSub")}</div>
          </div>
        </div>
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 12, display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 10, background: T.surface2, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>⚡</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.text }}>{t("mock.home.chapterTitle")}</div>
            <div style={{ fontSize: 10, color: T.textDim }}>{t("mock.home.chapterSub")}</div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default HomeMockup;
